import {
  DITypes,
  EPluggyTypes,
  IUserProfile,
  LocalStorageKeys
} from "@/core/@types/types";
import { injectable, inject } from "inversify";
import { PluggyConnect } from "pluggy-connect-sdk";
import { type IHandleWebservice } from "../handlers/handleCriarWebservice";
import { type PluggyGateway } from "./ports/PluggyGateway";
import { type IToastNotification } from "@/core/composables/notification/types";
import "reflect-metadata";
import {
  IContasResponseFin,
  ICriarWebserviceModel,
  IPluggyConnector,
  IResponseWebserviceModel
} from "./types";
import {
  ICallPluggySyncronizationPayload,
  ICallPluggySyncronizationResponse
} from "./ports/PluggyGateway";
import { type ISlackMessage } from "../../slackMessage/services/ports/SlackMessage";
import useStorage from "@/core/plugins/useStorage";
import { ElMessageBox } from "element-plus";

//CRIAR TESTES
@injectable()
export default class PluggyConnectorInit implements IPluggyConnector {
  @inject(EPluggyTypes.HANDLE_WEBSERVICE)
  private handleWebservice!: IHandleWebservice;

  @inject(EPluggyTypes.PLUGGY_GATEWAY)
  private pluggyGateway?: PluggyGateway;

  @inject(DITypes.NOTIFICATION)
  private notification!: IToastNotification<string>;

  @inject(DITypes.SLACK_MENSAGEM)
  private slackMensagem!: ISlackMessage;

  private storage = useStorage();

  private criarFilaDePromises() {
    let queue = Promise.resolve();
    const processed = new Set();

    const addPromise = (task: () => Promise<void>, key: string) => {
      if (processed.has(key)) {
        return queue;
      }

      processed.add(key);

      // Uma rejeição não pode impedir o processamento dos próximos eventos nem
      // bloquear uma nova tentativa da mesma chave.
      queue = queue
        .then(async () => {
          await task();
        })
        .catch(() => {
          processed.delete(key);
        });

      return queue;
    };

    return { addPromise };
  }

  private instaciarWidget(
    token: string,
    connectorId: number | undefined,
    contaId: string
  ) {
    // Regra de negócio do front-end legado em Global.js: criar/atualizar o
    // Webservice conforme os eventos emitidos pelo widget de conexão inicial.
    const EnviarMensagemSlack = (msg: string) => {
      this.slackMensagem.enviarMensagem({
        channel: "#widget-pluggy-logs",
        message: msg
      });
    };

    const { addPromise } = this.criarFilaDePromises();
    // Regra legada: controlar o erro separadamente para cada instância do widget.
    let onErrorDisplayed = false;

    const pluggyConnect = new PluggyConnect({
      connectToken: token,
      includeSandbox: false,
      selectedConnectorId: connectorId || undefined,
      allowConnectInBackground: false,
      onSuccess: (itemData) => {
        //notificar SLACK

        const user = this.storage.get<IUserProfile>(
          LocalStorageKeys.UserProfile
        );

        const successModel = {
          NomeDoGrupo: user?.Grupo,
          Banco: itemData?.item?.conector?.name,
          ContaBancariaId: contaId,
          ItemId: itemData?.item?.id,
          itemData: itemData?.item
        };

        if (itemData?.item?.id) {
          EnviarMensagemSlack(
            `Sucesso na conexão de conta via widget Pluggy: ${JSON.stringify(
              successModel
            )}`
          );
          addPromise(async () => {
            await this.handleWebservice.handleCriarWebservice(
              itemData.item.id,
              contaId,
              onErrorDisplayed,
              true,
              "onSuccess"
            );
          }, "onSuccess");
        } else {
          EnviarMensagemSlack(
            `Falso sucesso na conexão de conta via widget Pluggy: ${JSON.stringify(
              successModel
            )}`
          );
        }

        pluggyConnect.hide();
        pluggyConnect.destroy();
      },
      onEvent: (itemData) => {
        if (itemData.event == "ITEM_RESPONSE" && itemData?.item?.id) {
          addPromise(async () => {
            await this.handleWebservice.handleCriarWebservice(
              itemData.item.id,
              contaId,
              onErrorDisplayed,
              false,
              "onEvent"
            );
          }, "onEvent");
        }
      },
      onError: (error) => {
        //notificar SLACK

        const user = this.storage.get<IUserProfile>(
          LocalStorageKeys.UserProfile
        );

        const errorModel = {
          NomeDoGrupo: user?.Grupo,
          Conector: error?.data?.item?.connector?.name,
          ContaBancariaId: contaId,
          ItemId: error?.data?.item?.id,
          ErrorPluggy: error
        };

        EnviarMensagemSlack(
          `Erro na conexão de conta via widget Pluggy: ${JSON.stringify(
            errorModel
          )}`
        );

        console.error("Whoops! Pluggy Connect error... ", error);

        const data = error.data;
        onErrorDisplayed = true;

        const validErrors = ["USER_AUTHORIZATION_PENDING"];

        if (
          data?.item?.error?.code &&
          validErrors.includes(data.item.error.code) &&
          error?.data?.item?.id
        ) {
          addPromise(async () => {
            await this.handleWebservice.handleCriarWebservice(
              error?.data?.item?.id,
              contaId,
              onErrorDisplayed,
              true,
              "onError"
            );
          }, "onError");
        } else if (error?.data?.item?.id) {
          addPromise(async () => {
            await this.handleWebservice.handleCriarWebservice(
              error?.data?.item?.id,
              contaId,
              onErrorDisplayed,
              false,
              "onError"
            );
          }, "onError");
        }
      },
      onClose: () => {
        pluggyConnect.hide();
      }
    });

    pluggyConnect.init();
  }

  private instanciarWidgetAtualizacao(
    token: string,
    contaId: string,
    itemId: string,
    webserviceId: string,
    webserviceNotVisible: boolean = false
  ) {
    // Regra de negócio do front-end legado em Global.js: decidir quando
    // sincronizar, atualizar ou remover o Webservice durante a reconexão.
    const EnviarMensagemSlack = (msg: string) => {
      this.slackMensagem.enviarMensagem({
        channel: "#widget-pluggy-logs",
        message: msg
      });
    };

    const pluggyConnect = new PluggyConnect({
      connectToken: token,
      includeSandbox: false,
      allowConnectInBackground: false,
      updateItem: itemId,
      onSuccess: (itemData) => {
        if (itemId == null) return;

        void this.UpdateWebservicePluggy(
          itemData?.item?.id,
          contaId,
          webserviceId
        )
          .then((updated) => {
            if (updated) return;

            this.notification.showNotification(this.getLegacyConnectionError(), {
              type: "error",
              timeout: 3000
            });
          })
          .catch(() => {
            this.notification.showNotification(this.getLegacyConnectionError(), {
              type: "error",
              timeout: 3000
            });
          });

        pluggyConnect.hide();
        pluggyConnect.destroy();
      },
      onEvent: (itemData) => {
        if (itemId == null) return;

        if (itemData.event == "LOGIN_STEP_COMPLETED" && itemData?.item) {
          pluggyConnect.hide();

          return;
        }
      },
      onError: (error) => {
        const data = error.data;
        const validErrors = ["USER_AUTHORIZATION_PENDING"];

        if (
          itemId == null &&
          data?.item?.error?.code &&
          validErrors.includes(data.item.error.code)
        ) {
          void this.UpdateWebservicePluggy(
            error?.data?.item?.id,
            contaId,
            webserviceId
          )
            .then((updated) => {
              if (updated) return;

              this.notification.showNotification(this.getLegacyConnectionError(), {
                type: "error",
                timeout: 3000
              });
            })
            .catch(() => {
              this.notification.showNotification(this.getLegacyConnectionError(), {
                type: "error",
                timeout: 3000
              });
            });
        } else if (itemId != null && webserviceNotVisible == true) {
          void this.RemoveOnEventWebservicePluggy(itemId)
            .then((deletion) => {
              if (!deletion) pluggyConnect.hide();
            })
            .catch(() => pluggyConnect.hide());
        }

        const errorModel = {
          PostObj: webserviceId,
          Conector: error?.data?.item?.connector?.name,
          ContaBancariaId: contaId,
          ItemId: error?.data?.item?.id,
          ErrorPluggy: error
        };

        EnviarMensagemSlack(
          `Erro na conexão de conta via widget Pluggy: ${JSON.stringify(
            errorModel
          )}`
        );
      },
      onClose: () => {
        pluggyConnect.hide();
      }
    });

    pluggyConnect.init();
  }

  async RemoveOnEventWebservicePluggy(itemId: string): Promise<boolean> {
    try {
      const opResult =
        await this.pluggyGateway?.removeOnEventWebservicePluggy(itemId);
      if (!opResult?.Ok) {
        if (opResult?.Error) {
          this.notification.showNotification(opResult.Error, {
            type: "error",
            timeout: 3000
          });
        }

        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  async UpdateWebservicePluggy(
    itemId: string,
    conta: string,
    webservice: string
  ): Promise<boolean> {
    // Regra de negócio do front-end legado em Global.js: marcar a conexão
    // Pluggy como ativa após a autenticação do widget.
    // A edição do endpoint recebe um Webservice completo e substitui a
    // entidade persistida. Portanto, primeiro recuperamos o registro atual e
    // alteramos somente o estado do vínculo retornado pelo widget.
    const currentWebservice =
      await this.handleWebservice.handleObterWebservicePluggy(webservice);

    if (!currentWebservice?.Ok) return false;

    const model: ICriarWebserviceModel = {
      _id: currentWebservice._id,
      API: "203",
      ContaBancaria: currentWebservice.ContaBancaria ?? conta,
      Inativo: false,
      ApiPluggy: {
        ...currentWebservice.ApiPluggy,
        itemId: itemId,
        WebserviceNotVisible: false,
        needConnectWidget: false
      }
    };

    return await this.handleWebservice.handleEditarWebservice(model);
  }

  private getContaBancariaId(
    webservice: IResponseWebserviceModel
  ): string | undefined {
    const conta = webservice.ContaBancaria;

    if (typeof conta === "string" || typeof conta === "number") {
      return String(conta);
    }

    if (conta?._id) return String(conta._id);

    // Integridade de dados: accountId da Pluggy não substitui o ID interno da conta.
    return undefined;
  }

  private getErrorResponse(error = "") {
    return {
      Ok: false,
      Error: error
    } satisfies ICallPluggySyncronizationResponse;
  }

  private getLegacyConnectionError(): string {
    // Reutiliza a mensagem de conexão já apresentada pelo fluxo legado.
    return "Houve um erro ao conectar a sua conta! Por favor, tente novamente mais tarde.";
  }

  private hasWebserviceEditPermission(): boolean {
    // Regra legada: reconexão exige a permissão WebserviceAPI.Edit ou perfil administrador.
    const user = this.storage.get<IUserProfile>(LocalStorageKeys.UserProfile);

    if (user?.PerfilAdm === true) return true;

    const storedRoles = this.storage.get<unknown>(LocalStorageKeys.userRules);
    const roles = Array.isArray(storedRoles)
      ? storedRoles
      : typeof storedRoles === "string"
        ? storedRoles.split(",")
        : [];

    return roles.some(
      (role) => String(role).trim().toLocaleLowerCase() === "webserviceapi.edit"
    );
  }

  private async shouldConfirmReconnect(error?: string): Promise<boolean> {
    // Regra legada: solicitar confirmação somente quando o backend indicar reconexão.
    const normalizedError = error?.toLocaleLowerCase("pt-BR") ?? "";

    if (!normalizedError.includes("deseja conectar a conta?")) return false;

    try {
      await ElMessageBox.confirm(
        error ?? "Deseja conectar a conta?",
        "Atenção",
        {
          confirmButtonText: "Sim",
          cancelButtonText: "Não",
          type: "warning"
        }
      );

      return true;
    } catch {
      return false;
    }
  }

  private async sincronizarConta(
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    return (
      (await this.pluggyGateway?.callPluggySyncronization(params)) ??
      this.getErrorResponse()
    );
  }

  private async instanciarWidgetBuscaAtualizacao(
    token: string,
    contaId: string,
    itemId: string,
    webserviceId: string,
    webserviceNotVisible: boolean,
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    return new Promise((resolve) => {
      let closed = false;
      let resolved = false;
      let synchronizationInProgress = false;
      let synchronized = false;

      const finish = (response: ICallPluggySyncronizationResponse) => {
        if (resolved) return;

        resolved = true;
        resolve(response);
      };

      const showError = (message?: string) => {
        if (!message) return;

        this.notification.showNotification(message, {
          type: "error",
          timeout: 3000
        });
      };

      const synchronize = async (
        connectedItemId: string
      ): Promise<ICallPluggySyncronizationResponse> => {
        // Centraliza a atualização do vínculo e a sincronização após o widget.
        const updated = await this.UpdateWebservicePluggy(
          connectedItemId,
          contaId,
          webserviceId
        );

        if (!updated) return this.getErrorResponse();

        return this.sincronizarConta(params);
      };

      const pluggyConnect = new PluggyConnect({
        connectToken: token,
        includeSandbox: false,
        allowConnectInBackground: false,
        updateItem: itemId,
        onSuccess: async (itemData) => {
          if (closed || !itemData?.item?.id) return;

          if (synchronized) {
            try {
              await this.UpdateWebservicePluggy(
                itemData.item.id,
                contaId,
                webserviceId
              );
            } catch {
              // A sincronização já foi concluída; não reabre o fluxo por uma
              // atualização de estado posterior do widget.
            }
            pluggyConnect.hide();
            pluggyConnect.destroy();

            return;
          }

          if (synchronizationInProgress) return;

          synchronizationInProgress = true;
          try {
            const response = await synchronize(itemData.item.id);

            if (!response.Ok) {
              showError(response.Error);
              finish(response);

              return;
            }

            synchronized = true;
            finish(response);
            pluggyConnect.hide();
            pluggyConnect.destroy();
          } catch {
            const error = this.getLegacyConnectionError();
            this.notification.showNotification(error, {
              type: "error",
              timeout: 3000
            });
            finish(this.getErrorResponse(error));
          } finally {
            synchronizationInProgress = false;
          }
        },
        onEvent: (itemData) => {
          if (itemId == null) return;

          if (itemData.event === "LOGIN_STEP_COMPLETED" && itemData?.item) {
            pluggyConnect.hide();
          }
        },
        onError: async (error) => {
          if (closed || resolved) return;

          try {
            if (webserviceNotVisible) {
              const removed = await this.RemoveOnEventWebservicePluggy(itemId);
              if (!removed) pluggyConnect.hide();
            }

            const errorModel = {
              PostObj: webserviceId,
              Conector: error?.data?.item?.connector?.name,
              ContaBancariaId: contaId,
              ItemId: error?.data?.item?.id,
              ErrorPluggy: error
            };

            this.slackMensagem.enviarMensagem({
              channel: "#widget-pluggy-logs",
              message: `Erro na chamada de serviço via widget Pluggy: ${JSON.stringify(
                errorModel
              )}`
            });
          } catch {
            finish(this.getErrorResponse());
          }
        },
        onClose: () => {
          closed = true;
          pluggyConnect.hide();
          finish(this.getErrorResponse());
        }
      });

      pluggyConnect.init();
    });
  }

  private async instanciarWidgetBuscaCriacao(
    token: string,
    connectorId: number | undefined,
    contaId: string,
    webserviceId: string,
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    return new Promise((resolve) => {
      let closed = false;
      let resolved = false;
      let synchronizationInProgress = false;
      let synchronized = false;

      const finish = (response: ICallPluggySyncronizationResponse) => {
        if (resolved) return;

        resolved = true;
        resolve(response);
      };

      const synchronize = async (
        connectedItemId: string
      ): Promise<ICallPluggySyncronizationResponse> => {
        const updated = await this.UpdateWebservicePluggy(
          connectedItemId,
          contaId,
          webserviceId
        );

        if (!updated) return this.getErrorResponse();

        return this.sincronizarConta(params);
      };

      const pluggyConnect = new PluggyConnect({
        connectToken: token,
        includeSandbox: false,
        selectedConnectorId: connectorId || undefined,
        allowConnectInBackground: false,
        onSuccess: async (itemData) => {
          if (closed || !itemData?.item?.id) return;

          // LOGIN_STEP_COMPLETED pode concluir a sincronização antes do
          // onSuccess. Ainda assim, o item precisa ser persistido com o
          // callback de sucesso, como no fluxo legado.
          if (synchronized) {
            try {
              await this.UpdateWebservicePluggy(
                itemData.item.id,
                contaId,
                webserviceId
              );
            } catch {
              // A sincronização já foi concluída; não reabre o fluxo por uma
              // atualização de estado posterior do widget.
            }
            pluggyConnect.hide();
            pluggyConnect.destroy();

            return;
          }

          if (resolved) return;

          if (synchronizationInProgress) return;

          synchronizationInProgress = true;
          try {
            const response = await synchronize(itemData.item.id);

            if (!response.Ok) {
              if (response.Error) {
                this.notification.showNotification(response.Error, {
                  type: "error",
                  timeout: 3000
                });
              }

              finish(response);

              return;
            }

            finish(response);
            pluggyConnect.hide();
            pluggyConnect.destroy();
          } catch {
            const error = this.getLegacyConnectionError();
            this.notification.showNotification(error, {
              type: "error",
              timeout: 3000
            });
            finish(this.getErrorResponse(error));
          } finally {
            synchronizationInProgress = false;
          }
        },
        onEvent: async (itemData) => {
          if (
            closed ||
            resolved ||
            synchronized ||
            itemData.event !== "LOGIN_STEP_COMPLETED" ||
            !itemData?.item?.id
          ) {
            return;
          }

          if (synchronizationInProgress) return;

          synchronizationInProgress = true;
          try {
            const response = await synchronize(itemData.item.id);

            if (!response.Ok) {
              if (response.Error) {
                this.notification.showNotification(response.Error, {
                  type: "error",
                  timeout: 3000
                });
              }

              finish(response);

              return;
            }

            synchronized = true;
            pluggyConnect.hide();
            finish(response);
          } catch {
            const error = this.getLegacyConnectionError();
            this.notification.showNotification(error, {
              type: "error",
              timeout: 3000
            });
            finish(this.getErrorResponse(error));
          } finally {
            synchronizationInProgress = false;
          }
        },
        onError: async (error) => {
          const itemId = error?.data?.item?.id;
          if (closed || !itemId) return;

          const authorizationPending =
            error?.data?.item?.error?.code === "USER_AUTHORIZATION_PENDING";

          if (closed || resolved) return;
          let synchronizationStartedByError = false;

          try {
            if (authorizationPending && !synchronizationInProgress) {
              synchronizationInProgress = true;
              synchronizationStartedByError = true;
              const response = await synchronize(itemId);

              if (!response.Ok && response.Error) {
                this.notification.showNotification(response.Error, {
                  type: "error",
                  timeout: 3000
                });
              }

              if (response.Ok) synchronized = true;

              finish(response);
            }

            this.slackMensagem.enviarMensagem({
              channel: "#widget-pluggy-logs",
              message: `Erro na chamada de serviço via widget Pluggy: ${JSON.stringify(
                error
              )}`
            });
          } catch {
            const error = this.getLegacyConnectionError();
            this.notification.showNotification(error, {
              type: "error",
              timeout: 3000
            });
            finish(this.getErrorResponse(error));
          } finally {
            if (synchronizationStartedByError) {
              synchronizationInProgress = false;
            }
          }
        },
        onClose: () => {
          closed = true;
          pluggyConnect.hide();
          finish(this.getErrorResponse());
        }
      });

      pluggyConnect.init();
    });
  }

  private async reconectarConta(
    webservice: IResponseWebserviceModel,
    params: ICallPluggySyncronizationPayload,
    itemId?: string
  ): Promise<ICallPluggySyncronizationResponse> {
    // Regra de negócio do front-end legado em Global.js: validar o item e
    // escolher entre o widget de atualização e o widget de nova conexão.
    const contaId = this.getContaBancariaId(webservice);

    if (!contaId) {
      return this.getErrorResponse();
    }

    if (itemId) {
      const itemIsValid = await this.pluggyGateway?.validateExistingItemId(itemId);

      if (itemIsValid) {
        const token = await this.pluggyGateway?.obterConnectToken(itemId);

        if (!token) {
          return this.getErrorResponse("Erro ao conectar conta");
        }

        return this.instanciarWidgetBuscaAtualizacao(
          token,
          contaId,
          itemId,
          webservice._id,
          Boolean(webservice.ApiPluggy.WebserviceNotVisible),
          params
        );
      }
    }

    const token = await this.pluggyGateway?.obterConnectToken(null);
    if (!token) {
      return this.getErrorResponse("Erro ao conectar na conta");
    }

    const connectorId = await this.pluggyGateway?.obterConnectorId(contaId);

    if (connectorId == null) {
      return this.getErrorResponse(
        "O Open Finance não fornece serviço para o banco selecionado."
      );
    }

    return this.instanciarWidgetBuscaCriacao(
      token,
      connectorId,
      contaId,
      webservice._id,
      params
    );
  }

  private async executarBuscaSemWidget(
    webservice: IResponseWebserviceModel,
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    // Regra de negócio do front-end legado em Global.js: abrir o widget para
    // MFA/reconexão ou executar a sincronização diretamente.
    const apiPluggy = webservice.ApiPluggy;

    if (apiPluggy.hasMFA || apiPluggy.needConnectWidget) {
      return this.reconectarConta(webservice, params, apiPluggy.itemId ?? undefined);
    }

    return this.sincronizarConta(params);
  }

  async chamarServico(
    params: ICallPluggySyncronizationPayload
  ): Promise<ICallPluggySyncronizationResponse> {
    // Regra de negócio do front-end legado em Global.js: avaliar o estado da
    // conexão, tentar a conexão automática e iniciar a reconexão manual.
    const webservice = await this.handleWebservice.handleObterWebservicePluggy(
      params.serviceId
    );

    if (!webservice?.Ok) {
      return this.getErrorResponse(webservice?.Error);
    }

    const apiPluggy = webservice.ApiPluggy;
    const requiresConnectionHandling = Boolean(
      apiPluggy.itemDeleted ||
        apiPluggy.connectionInvalid ||
        apiPluggy.WebserviceNotVisible
    );

    if (!requiresConnectionHandling) {
      return this.executarBuscaSemWidget(webservice, params);
    }

    if (!this.hasWebserviceEditPermission()) {
      return this.getErrorResponse(
        "O Open Finance requer uma reconexão da conta, porém seu acesso não possui permissão para edição/atualização de Webservices. Solicite a um usuário Administrador do grupo permissão para Edição de Webservice ou para realizar esse processo."
      );
    }

    const contaId = this.getContaBancariaId(webservice);

    if (contaId) {
      const connected = await this.pluggyGateway?.connectOpenFinanceByAccount(
        contaId
      );

      if (connected) {
        const refreshedWebservice =
          await this.handleWebservice.handleObterWebservicePluggy(
            params.serviceId
          );

        if (refreshedWebservice?.Ok) {
          const refreshedApiPluggy = refreshedWebservice.ApiPluggy;
          const refreshedRequiresConnectionHandling = Boolean(
            refreshedApiPluggy.itemDeleted ||
              refreshedApiPluggy.connectionInvalid ||
              refreshedApiPluggy.WebserviceNotVisible
          );

          if (refreshedRequiresConnectionHandling) {
            const refreshedItemId = refreshedApiPluggy.WebserviceNotVisible
              ? refreshedApiPluggy.itemId ?? undefined
              : undefined;

            return this.reconectarConta(
              refreshedWebservice,
              params,
              refreshedItemId
            );
          }

          return this.executarBuscaSemWidget(refreshedWebservice, params);
        }
      }
    }

    if (apiPluggy.connectionInvalid && !apiPluggy.itemDeleted) {
      const validation = this.pluggyGateway?.validatePluggyAccountResult
        ? await this.pluggyGateway.validatePluggyAccountResult(webservice._id)
        : {
            Ok: Boolean(
              await this.pluggyGateway?.validatePluggyAccount(webservice._id)
            )
          };

      if (validation.Ok) {
        return this.executarBuscaSemWidget(webservice, params);
      }

      const errorMessage = validation.Error;
      const requiresConfirmation = errorMessage
        ?.toLocaleLowerCase("pt-BR")
        .includes("deseja conectar a conta?");

      if (!requiresConfirmation) {
        return this.getErrorResponse(errorMessage);
      }

      const shouldReconnect = await this.shouldConfirmReconnect(errorMessage);

      if (!shouldReconnect) {
        return this.getErrorResponse();
      }
    }

    const itemId = apiPluggy.WebserviceNotVisible
      ? apiPluggy.itemId ?? undefined
      : undefined;

    return this.reconectarConta(webservice, params, itemId);
  }

  hasCnpj(conta: IContasResponseFin | undefined): boolean | undefined {
    // Regra de negócio do front-end legado em Global.js: exigir CNPJ antes
    // de iniciar a conexão Open Finance.
    const hasCNPJ = conta?.Cnpj;

    if (!hasCNPJ) {
      this.notification.showNotification(
        "A conta selecionada não possui cadastrado um número de CNPJ. Por favor atualize suas informações na tela de Contas, para que seja possível conectar o Open Finance.",
        {
          type: "error",
          timeout: 3000
        }
      );

      return false;
    }

    return true;
  }

  async init(id: string) {
    // Regra de negócio do front-end legado em Global.js: tentar a conexão
    // automática antes de iniciar o widget Pluggy.
    const hasConta = await this.pluggyGateway?.obterConta(id);

    const hasCnpj = this.hasCnpj(hasConta);

    if (!hasCnpj) return;

    const connectByAccount =
      await this.pluggyGateway?.connectOpenFinanceByAccount(id);

    if (connectByAccount)
      return this.notification.showNotification(
        "Identificamos que uma de suas contas de Open Finance, possui também um vinculo com esta conta bancaria, e por isso realizamos a conexão automaticamente!",
        {
          type: "success",
          timeout: 3000
        }
      );

    const obterToken = await this.pluggyGateway?.obterConnectToken(
      connectByAccount || null
    );

    const obterConnectorId = await this.pluggyGateway?.obterConnectorId(id);

    if (obterConnectorId == null) {
      return this.notification.showNotification(
        "O Open Finance não fornece serviço para o banco selecionado.",
        {
          type: "info",
          timeout: 3000
        }
      );
    }

    const obterWebservice =
      await this.handleWebservice.handleObterWebservicePluggyPorConta(id);
    let updateItemId = null;
    let webserviceId = null;
    let webserviceNotVisible = false;

    if (
      obterWebservice != null &&
      obterWebservice?.Ok &&
      obterWebservice?.Error == undefined
    ) {
      updateItemId =
        obterWebservice.ApiPluggy.WebserviceNotVisible == true &&
        obterWebservice.ApiPluggy.itemId
          ? obterWebservice.ApiPluggy.itemId
          : null;

      webserviceId = obterWebservice._id;
      webserviceNotVisible = Boolean(
        obterWebservice.ApiPluggy.WebserviceNotVisible
      );
    }

    if (!obterToken) {
      return this.notification.showNotification(
        "Houve um erro ao autenticar a conta, tente novamente mais tarde e se o erro persistir, entre em contato com o suporte.",
        {
          type: "error",
          timeout: 3000
        }
      );
    } else {
      try {
        if (updateItemId != null && webserviceId != null) {
          const newConnectToken =
            await this.pluggyGateway?.obterConnectToken(updateItemId);
          if (!newConnectToken) {
            return this.notification.showNotification(
              "Houve um erro ao conectar a conta, tente novamente mais tarde.",
              {
                type: "error",
                timeout: 3000
              }
            );
          }

          this.instanciarWidgetAtualizacao(
            newConnectToken,
            id,
            updateItemId,
            webserviceId,
            webserviceNotVisible
          );
        } else {
          this.instaciarWidget(obterToken, obterConnectorId, id);
        }
      } catch (err) {
        console.log("Erro ao instanciar a pluggy: ", err);
      }
    }
  }
}
